/**
 * V2.0.51 — Reusable SEO + Open Graph wrapper.
 *
 * Mount on any public page to set <title>, meta description, canonical
 * URL, and the Twitter / Open Graph tags that drive the link previews
 * iMessage / Slack / Discord show.
 *
 * Usage:
 *   <SEO
 *     title="Local Code Finder — Blueprint AI"
 *     description="Free 90-second zoning report for any U.S. property."
 *     path="/local-code-finder"
 *   />
 *
 * The site name + base URL are hard-pinned so we never accidentally
 * advertise the preview domain. If you ever change the production
 * hostname, update SITE_URL below.
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://blueprintaillc.com";
const SITE_NAME = "Blueprint AI";
const DEFAULT_DESCRIPTION =
  "Plain-talk architectural design — 2D plans, 3D massing, IFC 4 BIM, and AI photo-realistic renders from a single canvas.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  themeColor,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Plain-Talk Architectural Design`;
  const url = `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {themeColor && <meta name="theme-color" content={themeColor} />}

      {/* Open Graph (Facebook / iMessage / Slack / LinkedIn) */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
